/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { loadIndustrySectors } from '../../actions/common';
import { SingleValue } from 'react-select';
import { Loader } from '@g17eco/atoms/loader';
import './styles.scss';
import { IndustryLevels } from '../../types/initiative';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { SelectFactory, SelectTypes, Option } from '@g17eco/molecules/select/SelectFactory';

export const getDefaultIcb2019 = (icb2019: IndustryLevels | undefined) => {
  const industryLevelValues = Object.values(icb2019 || {});
  const [defaultIndustry] = industryLevelValues.sort().reverse();
  return defaultIndustry || '';
};

export const getIndustryLevels = (code: string) => ({
  level1: code.length >= 2 ? code.substring(0, 2) : '',
  level2: code.length >= 4 ? code.substring(0, 4) : '',
  level3: code.length >= 6 ? code.substring(0, 6) : '',
  level4: code.length === 8 ? code : '',
});

const getSubCode = (code: string, length: number) => code.substring(0, length);

interface IndustrySectorSelectorProps {
  industry?: string;
  handleIndustrySector: (value: IndustryLevels) => void;
}

const IndustrySectorSelector = ({ industry, handleIndustrySector }: IndustrySectorSelectorProps) => {
  const dispatch = useAppDispatch();
  const industrySectorsState = useAppSelector((state) => state.industrySectors);

  const [industrySector, setIndustrySector] = useState<string>('');

  useEffect(() => {
    setIndustrySector(industry ?? '');
    dispatch(loadIndustrySectors());
  }, [dispatch, industry]);

  const handleChange = (selectedOption: SingleValue<Option>) => {
    const code = selectedOption?.value ?? '';

    setIndustrySector(code);
    handleIndustrySector(getIndustryLevels(code));
  };

  const renderLevel = (level: number) => {
    const industrySectors = industrySectorsState.data ?? [];
    const codeLength = level * 2;
    const parentLength = codeLength - 2;
    const currentSector = getSubCode(industrySector, codeLength);
    const parentSector = getSubCode(industrySector, parentLength);
    const filteredIndustrySectors = industrySectors.filter(
      (sector) => sector.code.length === codeLength && getSubCode(sector.code, parentLength) === parentSector,
    );
    const options = filteredIndustrySectors.map((item) => ({ value: item.code, label: item.name }));
    const selectedOption = options.find((item) => item.value === currentSector) || null;
    return (
      <SelectFactory
        selectType={SelectTypes.SingleSelect}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        className='mb-2 text-left'
      />
    );
  };

  if (!industrySectorsState.loaded) {
    return <Loader />;
  }

  const currentSectorLength = industrySector.length;

  return (
    <div className='industrySectorSelectors'>
      <Label>Industry</Label>
      {renderLevel(1)}
      {currentSectorLength >= 2 ? (
        <>
          <Label>Supersector</Label>
          {renderLevel(2)}
        </>
      ) : (
        <></>
      )}
      {currentSectorLength >= 4 ? (
        <>
          <Label>Sector</Label>
          {renderLevel(3)}
        </>
      ) : (
        <></>
      )}
      {currentSectorLength >= 6 ? (
        <>
          <Label>Subsector</Label>
          {renderLevel(4)}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default IndustrySectorSelector;
